import { makeStyles, createStyles, Theme } from '@material-ui/core';

// ─────────────────────────────────────────────────────────────────────────────
// COLOURS
// ─────────────────────────────────────────────────────────────────────────────

export const COLORS = {
  brand: {
    primary: '#003f2d', // CBRE dark green
    primaryDark: '#002d1f',
    secondary: '#1d5c3c',
  },
  environment: {
    dev: { bg: '#e6f2ff', text: '#0c4a6e', border: '#b3d9ff' },
    sit: { bg: '#f0e6ff', text: '#312e81', border: '#d9b3ff' },
    qa: { bg: '#e6f0ff', text: '#1e3a8a', border: '#b3d4ff' },
    uat: { bg: '#fff0e6', text: '#854d0e', border: '#ffd4b3' },
    nonProd: { bg: '#eaf3de', text: '#15803d', border: '#c6dfc6' },
  },
  status: {
    success: { bg: '#e6f4ec', text: '#1d5c3c', border: '#50af78' },
    failed: { bg: '#fff7f5', text: '#1a1a1a', border: '#ff543e' },
    warning: { bg: '#fff8e6', text: '#92400e', border: '#f59e0b' },
  },
  neutral: {
    white: '#ffffff',
    black: '#1a1a1a',
    grey50: '#f9fafb',
    grey100: '#f0f4f8',
    grey200: '#e8eef5',
    grey300: '#e0e0e0',
    grey400: '#cbd5e0',
    grey500: '#a0aec0',
    grey600: '#718096',
    grey700: '#4a5563',
    grey800: '#2d3748',
  },
  info: {
    bg: '#e6f2ff',
    text: '#1e40af',
    icon: '#4a5fc1',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// SPACING
// ─────────────────────────────────────────────────────────────────────────────

export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TYPOGRAPHY
// ─────────────────────────────────────────────────────────────────────────────

export const TYPOGRAPHY = {
  fontFamily:
    '"Calibre-R", "Calibre-R-Regular", -apple-system, Roboto, Helvetica, sans-serif',
  sizes: {
    xs: '10px',
    sm: '12px',
    base: '13px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
    '3xl': '28px',
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// SHADOWS
// ─────────────────────────────────────────────────────────────────────────────

export const SHADOW = {
  sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
  md: '0 2px 8px rgba(0, 0, 0, 0.12)',
  lg: '0 10px 40px rgba(0, 0, 0, 0.1)',
} as const;

const getBadgeTokens = (isDark: boolean) => ({
  '--badge-dev-bg': isDark ? '#1e3a5f' : '#e6f2ff',
  '--badge-dev-text': isDark ? '#64b5f6' : '#0c4a6e',
  '--badge-dev-border': isDark ? '#2c5282' : '#b3d9ff',

  '--badge-sit-bg': isDark ? '#3d2c5f' : '#f0e6ff',
  '--badge-sit-text': isDark ? '#d4a5ff' : '#312e81',
  '--badge-sit-border': isDark ? '#4a3a6f' : '#d9b3ff',

  '--badge-qa-bg': isDark ? '#1e3a5f' : '#e6f0ff',
  '--badge-qa-text': isDark ? '#64b5f6' : '#1e3a8a',
  '--badge-qa-border': isDark ? '#2c5282' : '#b3d4ff',

  '--badge-uat-bg': isDark ? '#5f3f1e' : '#fff0e6',
  '--badge-uat-text': isDark ? '#ffb366' : '#854d0e',
  '--badge-uat-border': isDark ? '#6f4f2e' : '#ffd4b3',

  '--badge-nonprod-bg': isDark ? '#2d4a1f' : '#eaf3de',
  '--badge-nonprod-text': isDark ? '#5dd583' : '#15803d',
  '--badge-nonprod-border': isDark ? '#3d5a2f' : '#c6dfc6',

  '--pc-badge-failed-bg': isDark ? '#3d2a2a' : '#fff7f5',
  '--pc-badge-failed-text': isDark ? '#ff7875' : '#1a1a1a',
  '--pc-badge-failed-border': '#ff543e',

  // Stop Run action accent (button fill + dialog confirm)
  '--ec-stop-run-accent': isDark ? '#FF381F' : '#D03B2D',
  '--pc-badge-generate-failed-bg': isDark ? '#3d2a2a' : '#fee2e2',
  '--pc-badge-generate-failed-text': isDark ? '#ff7875' : '#991b1b',
  '--pc-badge-generate-failed-border': isDark ? 'transparent' : '#fecaca',
  '--pc-badge-generate-failed-border-width': isDark ? '0' : '1px',
  '--pc-badge-generate-failed-icon': isDark ? '#ff543e' : '#991b1b',
  '--pc-error-compact-bg': isDark ? '#3d2a2a' : '#fff7f5',

  '--pc-badge-ready-bg': isDark ? '#2d4a3f' : '#e6f4ec',
  '--pc-badge-ready-text': isDark ? '#5dd583' : '#1d5c3c',
  '--pc-badge-ready-border': '#50af78',

  '--pc-badge-generate-successful-bg': isDark ? '#0c2a2a' : '#e6fbf8',
  '--pc-badge-generate-successful-text': isDark ? '#4adecd' : '#0c4a3e',
  '--pc-badge-generate-successful-border': isDark ? '#1a4a4a' : '#b3ede6',

  '--pc-badge-submitted-bg': isDark ? '#1e3a5f' : '#e6f2ff',
  '--pc-badge-submitted-text': isDark ? '#64b5f6' : '#0c4a6e',
  '--pc-badge-submitted-border': isDark ? '#2c5282' : '#b3d9ff',

  '--pc-badge-processing-bg': isDark ? '#5a3a1f' : '#fff8e6',
  '--pc-badge-processing-text': isDark ? '#ffb74d' : '#92400e',
  '--pc-badge-processing-border': isDark ? '#7a4e1f' : '#ffd699',

  // Status pill colors for StatusBadge component
  '--pc-status-ready-bg': isDark ? '#166534' : '#F0FFF4',
  '--pc-status-ready-text': isDark ? '#86EFAC' : '#166534',
  '--pc-status-ready-border': isDark ? '#166534' : '#86EFAC',

  '--pc-status-submitted-bg': isDark ? '#166534' : '#F0FFF4',
  '--pc-status-submitted-text': isDark ? '#86EFAC' : '#166534',
  '--pc-status-submitted-border': isDark ? '#166534' : '#86EFAC',

  '--pc-status-processing-bg': isDark ? '#7C4A00' : '#FFF7E6',
  '--pc-status-processing-text': isDark ? '#FFD99E' : '#7C4A00',
  '--pc-status-processing-border': isDark ? '#7C4A00' : '#FFD99E',

  '--pc-status-generate-successful-bg': isDark ? '#0C4A3E' : '#E6FBF8',
  '--pc-status-generate-successful-text': isDark ? '#B3EDE6' : '#0C4A3E',
  '--pc-status-generate-successful-border': isDark ? '#0C4A3E' : '#B3EDE6',

  '--pc-status-execute-successful-bg': isDark ? '#0C4A3E' : '#E6FBF8',
  '--pc-status-execute-successful-text': isDark ? '#B3EDE6' : '#0C4A3E',
  '--pc-status-execute-successful-border': isDark ? '#0C4A3E' : '#B3EDE6',

  '--pc-status-failed-bg': isDark ? '#991B1B' : '#FEE2E2',
  '--pc-status-failed-text': isDark ? '#FECACA' : '#991B1B',
  '--pc-status-failed-border': isDark ? '#991B1B' : '#FECACA',

  '--pc-status-generate-failed-bg': isDark ? '#991B1B' : '#FEE2E2',
  '--pc-status-generate-failed-text': isDark ? '#FECACA' : '#991B1B',
  '--pc-status-generate-failed-border': isDark ? '#991B1B' : '#FECACA',
});

const getStepperTokens = (isDark: boolean) => ({
  '--ec-stepper-node-active-bg': isDark ? '#2d4a3f' : '#e6f2ff',
  '--ec-stepper-node-active-color': isDark
    ? 'var(--iqe-primary-main)'
    : '#4a90e2',
  '--ec-stepper-node-active-border': isDark
    ? 'var(--iqe-primary-main)'
    : '#4a90e2',

  '--ec-stepper-node-completed-bg': isDark ? '#2d4a3f' : '#e6f7ed',
  '--ec-stepper-node-completed-color': 'var(--iqe-primary-main)',
  '--ec-stepper-node-completed-border': 'var(--iqe-primary-main)',

  '--ec-stepper-node-pending-bg': isDark ? '#424242' : '#d6d7db',
  '--ec-stepper-node-pending-color': isDark ? '#A1A19C' : '#cbd5e0',
  '--ec-stepper-node-pending-border': isDark ? '#505050' : '#e8eef5',

  '--ec-stepper-node-connector-completed': 'var(--iqe-primary-main)',
});

const getTestCaseStatusTokens = (isDark: boolean) => ({
  '--tc-passed-bg': isDark ? '#2d4a3f' : '#e6f4ec',
  '--tc-running-bg': isDark ? '#2d4a3f' : '#e6f4ec',
  '--tc-pending-bg': isDark ? 'var(--iqe-bg-paper)' : '#e6f4ec',

  '--tc-success-icon-text': 'var(--iqe-primary-main)',
  '--tc-analysis-complete-icon-svg': isDark ? '#1a1a1a' : '#ffffff',
});

const getPaletteTokens = (theme: Theme) => ({
  '--iqe-bg-paper': theme.palette.background.paper,
  '--iqe-bg-default': theme.palette.background.default,
  '--iqe-divider': theme.palette.divider,
  '--iqe-border': (theme.palette as any).border,
  '--iqe-primary-main': theme.palette.primary.main,
  '--iqe-text-primary': theme.palette.text.primary,
  '--iqe-text-secondary': theme.palette.text.secondary,
  '--iqe-text-tertiary': (theme.palette.text as any).tertiary,
  '--iqe-text-muted': (theme.palette.text as any).muted,
  '--iqe-primary-dark': theme.palette.primary.dark,
  '--iqe-text-contrast': (theme.palette.text as any).contrast,
  '--iqe-primary-contrast-text': theme.palette.primary.contrastText,
  '--iqe-action-hover': theme.palette.action.hover,
  '--iqe-error': theme.palette.error.main,
  '--iqe-warning': theme.palette.warning.main,

  '--iqe-hint-bg':
    theme.palette.type === 'dark' ? theme.palette.background.paper : '#e6f2ff',
  '--iqe-bg-highlight':
    theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(0, 63, 45, 0.12)',
});

export const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === 'dark';

  return createStyles({
    runLogsDialogPaper: {
      ...getPaletteTokens(theme),
      '--run-logs-spacing-small': `${theme.spacing(1)}px`,
      '--run-logs-spacing-medium': `${theme.spacing(2)}px`,
      '--run-logs-spacing-large': `${theme.spacing(3)}px`,
      '--run-logs-table-max-height': `${theme.spacing(50)}px`,
    } as any,
    pageNavRow: {
      marginTop: theme.spacing(2),
    },
    adminActionHint: {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1.4,
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
      margin: `${theme.spacing(0.5)}px 0 0`,
      maxWidth: 'none',
      width: '100%',
    },
    adminActionHintIcon: {
      color: theme.palette.text.secondary,
      flexShrink: 0,
      fontSize: theme.typography.pxToRem(16),
      marginTop: theme.spacing(0.125),
    },
    adminActionHintText: {
      minWidth: 0,
      maxWidth: '100%',
      overflowWrap: 'anywhere',
    },
    adminActionHintCentered: {
      justifyContent: 'center',
      textAlign: 'center',
      maxWidth: theme.spacing(68),
    },

    // ── EmptyProjectsState ──────────────────────────────────────────────────
    // styles/emptyProjectsState.css

    emptyProjectsStateRoot: {
      '--eps-icon-box-bg': isDark ? 'rgba(23, 232, 143, 0.2)' : '#eaf3de',
      ...getPaletteTokens(theme),
    } as any,

    // ── ProjectsHeader ──────────────────────────────────────────────────────
    // styles/projectsHeader.css

    projectsHeaderRoot: {
      ...getPaletteTokens(theme),
    } as any,

    // shared with ProjectDetails
    projectsHeaderTitle: {
      margin: `0 0 ${theme.spacing(0.1)}px 0`,
      padding: 0,
      fontSize: '28px',
      fontWeight: 700,
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      flex: '0 0 auto',
      lineHeight: 1.235,
    },

    // ── AddProjectModal ──────────────────────────────────────────────────────
    // styles/addProjectModal.css

    addProjectModalPaper: {
      ...getPaletteTokens(theme),
      '--apm-ack-bg': isDark ? '#333333' : '#e6f2ff',
      '--apm-info': '#4a5fc1',
    } as any,

    // ── ProjectsGrid ────────────────────────────────────────────────────────
    // styles/projectsGrid.css

    // ── ProjectsTable ───────────────────────────────────────────────────────
    // Pagination-related styles
    tableHead: {
      backgroundColor: theme.palette.background.default,
    },
    tableHeadCell: {
      backgroundColor: theme.palette.background.default,
      fontWeight: 600,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableBodyCell: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    configTableRow: {
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: isDark
          ? 'rgba(255, 255, 255, 0.12)'
          : 'rgba(0, 63, 45, 0.12)',
      },
    },
    table: {
      backgroundColor: theme.palette.background.paper,
    },
    tablePaginationRoot: {
      overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
    },
    tablePaginationActions: {
      marginLeft: theme.spacing(2),
    },
    tablePaginationSelect: {
      color: theme.palette.text.secondary,
    },
    tablePaginationBar: {
      flex: '1 1 100%',
    },
    flexEnd: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    gap1: {
      gap: theme.spacing(1),
    },

    // common/styles/projectInfoPopover.css
    projectInfoPopoverPaper: {
      ...getPaletteTokens(theme),
    } as any,

    // ── RegenerateConfirmDialog ─────────────────────────────────────────────
    // styles/regenerateConfirmDialog.css

    regenerateDialogPaper: {
      '--rcd-text-primary': theme.palette.text.primary,
      '--rcd-text-secondary': theme.palette.text.secondary,
      '--rcd-bg-default': theme.palette.background.default,
      '--rcd-divider': theme.palette.divider,
    } as any,

    stopRunDialogPaper: {
      ...getPaletteTokens(theme),
      ...getBadgeTokens(isDark),
    } as any,

    // ── ProjectCard ─────────────────────────────────────────────────────────
    // styles/projectCard.css

    projectCard: {
      ...getPaletteTokens(theme),
      ...getBadgeTokens(isDark),
      padding: theme.spacing(2),
      '--pc-project-name-color': isDark
        ? '#f1f5f9'
        : theme.palette.primary.main,
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
      },
    } as any,

    // ── ProjectDetails ───────────────────────────────────────────────────────
    // styles/projectDetails.css

    projectDetailsPage: {
      '--pd-empty-icon-bg': isDark ? 'rgba(23,232,143,0.2)' : '#eaf3de',
      ...getPaletteTokens(theme),
      ...getBadgeTokens(isDark),
    } as any,

    pdRunStatusContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      maxWidth: '100%',
    },
    pdRunStatusValue: {
      display: 'grid',
      gridTemplateColumns: `${theme.spacing(15)}px ${theme.spacing(3)}px`,
      alignItems: 'center',
      gap: theme.spacing(1),
      maxWidth: '100%',
    },
    pdRunStatusReasonSlot: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    pdRunCardOperationLabel: {
      whiteSpace: 'nowrap',
      flexShrink: 0,
      minWidth: 'max-content',
    },
    pdRunCardSuccessful: {
      borderLeft: `${theme.spacing(0.5)}px solid ${theme.palette.primary.main}`,
    },
    pdRunCardFailed: {
      borderLeft: `${theme.spacing(0.5)}px solid var(--pc-status-failed-text)`,
    },
    pdRunCardStopped: {
      borderLeft: `${theme.spacing(0.5)}px solid var(--ec-stop-run-accent)`,
    },
    pdRunCardStatusReason: {
      width: '100%',
    },
    pdRunStatusReasonTrigger: {
      color: theme.palette.primary.main,
      padding: theme.spacing(0.25),
    },
    pdRunStatusReasonPopoverPaper: {
      ...getPaletteTokens(theme),
      padding: theme.spacing(1.5),
      maxWidth: theme.spacing(35),
      border: `1px solid ${theme.palette.divider}`,
    } as any,
    pdRunStatusReasonText: {
      color: (theme.palette.text as any).muted,
      fontSize: '14px',
      lineHeight: 1.5,
      overflowWrap: 'anywhere',
    },
    pdRunStatusReasonInline: {
      display: 'block',
      marginTop: theme.spacing(0.5),
      color: (theme.palette.text as any).muted,
      fontSize: '13px',
      lineHeight: 1.4,
      overflowWrap: 'anywhere',
    },

    // ── ExecutionCenter ──────────────────────────────────────────────────────
    // styles/executionCenter.css

    executionCenter: {
      ...getStepperTokens(isDark),
      ...getTestCaseStatusTokens(isDark),
      ...getPaletteTokens(theme),
      ...getBadgeTokens(isDark),
      padding: theme.spacing(3),
      '--apm-info': '#4a5fc1',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
      },
    } as any,

    // ── EnhanceTestPlanModal ────────────────────────────────────────────────
    // styles/enhanceTestPlanModal.css

    enhanceTestPlanPaper: {
      ...getPaletteTokens(theme),
    } as any,

    // ── ResultsWorkspace ─────────────────────────────────────────────────────
    // styles/resultsWorkspace.css
    resultsWorkspace: {
      '--rw-bg-paper': theme.palette.background.paper,
      '--rw-bg-default': theme.palette.background.default,
      '--rw-divider': theme.palette.divider,
      '--rw-text-primary': theme.palette.text.primary,
      '--rw-text-secondary': theme.palette.text.secondary,
      '--rw-action-hover': theme.palette.action.hover,
      '--rw-primary-main': theme.palette.primary.main,
    } as any,
  });
});
